/**
 * Ícone + cor do tema por LOINC para cards no estilo FitnessDataModal (atendimento wearable).
 */
import type { ElementType } from "react";
import {
  AccessTime,
  Air,
  Bedtime,
  Bloodtype,
  DirectionsRun,
  Favorite,
  Hotel,
  Layers,
  LocalFireDepartment,
  MonitorHeart,
  MonitorWeight,
  Nightlight,
  Psychology,
  Straighten,
  WaterDrop,
} from "@mui/icons-material";
import type { Theme } from "@mui/material/styles";

import {
  WEARABLE_LOINC_LIST,
  type WearableShareKey,
} from "@/services/fhir/observation/wearableObservationBuilder";

const METRIC_PALETTE_KEY: Record<WearableShareKey, keyof Theme["palette"]> = {
  steps: "secondary600",
  calories: "warning600",
  distance: "secondary800",
  heartRate: "error500",
  weight: "neutral700",
  activeMinutes: "primary600",
  glucose: "secondary700",
  bloodPressure: "neutral700",
  sleep: "primary900",
};

const METRIC_ICON_BY_KEY: Record<WearableShareKey, ElementType> = {
  steps: DirectionsRun,
  calories: LocalFireDepartment,
  distance: Straighten,
  heartRate: Favorite,
  weight: MonitorWeight,
  activeMinutes: AccessTime,
  glucose: Bloodtype,
  bloodPressure: MonitorHeart,
  sleep: Bedtime,
};

/** LOINCs extras (água, estágios do sono, SpO2, peso, pele) — ícone + cor da palette Theme.tsx. */
const EXTRA_LOINC_META: Record<
  string,
  { icon: ElementType; paletteKey: keyof Theme["palette"] }
> = {
  "8999-5": { icon: WaterDrop, paletteKey: "secondary500" },
  "93828-2": { icon: Nightlight, paletteKey: "warning500" },
  "93829-0": { icon: Psychology, paletteKey: "primary700" },
  "93830-8": { icon: Hotel, paletteKey: "secondary400" },
  "93831-6": { icon: Layers, paletteKey: "primary900" },
  "59408-5": { icon: Air, paletteKey: "error500" },
  "96634-8": { icon: Air, paletteKey: "error600" },
  "29463-7": { icon: MonitorWeight, paletteKey: "neutral700" },
  "81264-4": { icon: Favorite, paletteKey: "secondary700" },
};

export function getPrimaryLoincCode(resource: any): string | undefined {
  const codings = resource?.code?.coding;
  if (!Array.isArray(codings)) return undefined;
  const fromLoinc = codings.find(
    (c: any) =>
      typeof c?.system === "string" &&
      (c.system.includes("loinc.org") || c.system === "http://loinc.org")
  );
  return fromLoinc?.code ?? codings[0]?.code;
}

function getPaletteMain(theme: Theme, key: keyof Theme["palette"]): string {
  const entry = theme.palette[key] as { main?: string } | undefined;
  return entry?.main ?? theme.palette.neutral600.main;
}

export function getWearableMedicalCardPresentation(
  resource: any,
  theme: Theme
): { label: string; icon: ElementType; color: string } {
  const loinc = getPrimaryLoincCode(resource);
  const fallbackLabel =
    resource?.code?.text ||
    resource?.code?.coding?.[0]?.display ||
    "Observação";

  if (!loinc) {
    return { label: fallbackLabel, icon: MonitorHeart, color: theme.palette.neutral600.main };
  }

  const def = WEARABLE_LOINC_LIST.find((d) => d.loinc === loinc);
  if (def) {
    return {
      label: def.text,
      icon: METRIC_ICON_BY_KEY[def.key],
      color: getPaletteMain(theme, METRIC_PALETTE_KEY[def.key]),
    };
  }

  const extra = EXTRA_LOINC_META[String(loinc)];
  if (extra) {
    return {
      label: fallbackLabel,
      icon: extra.icon,
      color: getPaletteMain(theme, extra.paletteKey),
    };
  }

  return {
    label: fallbackLabel,
    icon: MonitorHeart,
    color: theme.palette.neutral600.main,
  };
}
